import React from 'react';
import { StructuredData } from 'components/SEO';
import RankedCardList from 'components/RankedCardList/RankedCardList';
import { rankedCardHref } from 'components/RankedCardList/RankedCardRow';
import { SeoCardRow, SeoPageStats } from 'store/services/seo/publicSeoApi';
import './SeoTradeData.css';

interface Props {
  /** The set or format name used in the data sentence. */
  name: string;
  stats: SeoPageStats;
  computedAt: string;
  mostWanted: SeoCardRow[];
  mostTraded: SeoCardRow[];
  mostAvailable: SeoCardRow[];
  /** Page URL for the ItemList JSON-LD. */
  pageUrl: string;
  /** Optional intro paragraph above the data sentence (format pages). */
  intro?: string;
}

/** The ticket's data sentence: numbers only, no prose generation. */
export const dataSentence = (name: string, stats: SeoPageStats): string =>
  `${stats.wantUsers.toLocaleString()} TradeMagic traders want cards from ${name}, and ${stats.tradeCount90d.toLocaleString()} completed trades in the last 90 days included one.`;

/** Meta description: the data sentence cut to 160 characters. */
export const seoDescriptionFrom = (sentence: string): string =>
  sentence.length <= 160 ? sentence : `${sentence.slice(0, 157).trimEnd()}...`;

export const formatUpdated = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

/** The ItemList JSON-LD of the Most Wanted cards, shared by the set and format pages. */
export const mostWantedItemList = (name: string, mostWanted: SeoCardRow[], pageUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: `Most wanted ${name} cards on TradeMagic`,
  url: pageUrl,
  numberOfItems: mostWanted.length,
  itemListElement: mostWanted.map((row, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: row.name,
    url: `https://trademagic.gg${rankedCardHref(row)}`,
  })),
});

/**
 * The live-trade-data block shared by set and format landing pages (Asana
 * 1219287567650303): data sentence, "Updated {date}", the three ranked lists
 * and one ItemList of the Most Wanted cards.
 */
const SeoTradeData: React.FC<Props> = ({ name, stats, computedAt, mostWanted, mostTraded, mostAvailable, pageUrl, intro }) => {
  const itemList = mostWantedItemList(name, mostWanted, pageUrl);
  return (
    <section className="seo-trade-data" data-testid="seo-trade-data">
      {mostWanted.length > 0 && <StructuredData data={itemList} />}
      {intro && <p className="seo-trade-data__intro">{intro}</p>}
      <p className="seo-trade-data__sentence" data-testid="seo-data-sentence">
        {dataSentence(name, stats)}
      </p>
      <p className="seo-trade-data__updated">
        Updated <time dateTime={computedAt}>{formatUpdated(computedAt)}</time>
      </p>
      <RankedCardList
        title="Most Wanted"
        rows={mostWanted}
        countLabel={(n) => `${n.toLocaleString()} ${n === 1 ? 'trader wants it' : 'traders want it'}`}
        emptyText="No one has this on a want list yet."
      />
      <RankedCardList
        title="Most Traded, last 90 days"
        rows={mostTraded}
        countLabel={(n) => `${n.toLocaleString()} ${n === 1 ? 'trade' : 'trades'}`}
        emptyText="No completed trades in the last 90 days."
      />
      <RankedCardList
        title="Most Available"
        rows={mostAvailable}
        countLabel={(n) => `${n.toLocaleString()} ${n === 1 ? 'copy listed' : 'copies listed'}`}
        emptyText="No copies are listed for trade yet."
      />
    </section>
  );
};

export default SeoTradeData;
